import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useMarketingStore = create(
  persist(
    (set, get) => ({
      // Marketing Platforms with costs
      platforms: [
        { id: 'facebook', name: 'Facebook', icon: '👍', color: '#1877F2', spent: 2500, leads: 145, roi: 3.2 },
        { id: 'google', name: 'Google Ads', icon: '🔍', color: '#4285F4', spent: 3200, leads: 189, roi: 4.1 },
        { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0A66C2', spent: 1800, leads: 67, roi: 2.8 },
        { id: 'instagram', name: 'Instagram', icon: '📸', color: '#E4405F', spent: 1200, leads: 92, roi: 2.5 },
        { id: 'twitter', name: 'Twitter', icon: '𝕏', color: '#000000', spent: 600, leads: 34, roi: 1.9 },
        { id: 'tiktok', name: 'TikTok', icon: '🎵', color: '#000000', spent: 900, leads: 156, roi: 5.2 },
      ],

      // Marketing campaigns
      campaigns: [
        { id: 'camp-1', name: 'Q2 Brand Awareness', platform: 'facebook', budget: 5000, spent: 2500, leads: 145, status: 'active', startDate: '2026-04-01', endDate: '2026-06-30' },
        { id: 'camp-2', name: 'B2B Lead Generation', platform: 'linkedin', budget: 3000, spent: 1800, leads: 67, status: 'active', startDate: '2026-04-15', endDate: '2026-07-15' },
        { id: 'camp-3', name: 'Social Media Growth', platform: 'instagram', budget: 2000, spent: 1200, leads: 92, status: 'active', startDate: '2026-04-10', endDate: '2026-06-10' },
      ],

      // Monthly marketing spend data
      monthlySpend: [
        { month: 'Jan', facebook: 1800, google: 2400, linkedin: 1200, instagram: 800, twitter: 300, tiktok: 500 },
        { month: 'Feb', facebook: 2000, google: 2600, linkedin: 1400, instagram: 900, twitter: 350, tiktok: 600 },
        { month: 'Mar', facebook: 2200, google: 2800, linkedin: 1600, instagram: 1000, twitter: 400, tiktok: 700 },
        { month: 'Apr', facebook: 2500, google: 3200, linkedin: 1800, instagram: 1200, twitter: 600, tiktok: 900 },
        { month: 'May', facebook: 2700, google: 3400, linkedin: 1900, instagram: 1300, twitter: 650, tiktok: 1000 },
        { month: 'Jun', facebook: 2900, google: 3600, linkedin: 2100, instagram: 1400, twitter: 700, tiktok: 1100 },
      ],

      // Budget allocations
      budgets: {
        monthly: 15000,
        quarterly: 45000,
        annual: 180000,
      },

      // PLATFORM OPERATIONS
      addPlatform: (platformData) => {
        set((state) => ({
          platforms: [
            ...state.platforms,
            {
              id: `platform-${Date.now()}`,
              ...platformData,
            },
          ],
        }))
      },

      updatePlatform: (platformId, updates) => {
        set((state) => ({
          platforms: state.platforms.map((p) =>
            p.id === platformId ? { ...p, ...updates } : p
          ),
        }))
      },

      deletePlatform: (platformId) => {
        set((state) => ({
          platforms: state.platforms.filter((p) => p.id !== platformId),
        }))
      },

      // CAMPAIGN OPERATIONS
      addCampaign: (campaignData) => {
        set((state) => ({
          campaigns: [
            ...state.campaigns,
            {
              id: `camp-${Date.now()}`,
              status: 'active',
              ...campaignData,
            },
          ],
        }))
      },

      updateCampaign: (campaignId, updates) => {
        set((state) => ({
          campaigns: state.campaigns.map((c) =>
            c.id === campaignId ? { ...c, ...updates } : c
          ),
        }))
      },

      deleteCampaign: (campaignId) => {
        set((state) => ({
          campaigns: state.campaigns.filter((c) => c.id !== campaignId),
        }))
      },

      // BUDGET OPERATIONS
      updateBudgets: (budgetData) => {
        set((state) => ({
          budgets: { ...state.budgets, ...budgetData },
        }))
      },

      // ADD COST TO PLATFORM
      addCostToPlatform: (platformId, cost, leads = 0) => {
        set((state) => ({
          platforms: state.platforms.map((p) =>
            p.id === platformId
              ? {
                  ...p,
                  spent: p.spent + cost,
                  leads: p.leads + leads,
                  roi: parseFloat(((p.leads + leads) / (p.spent + cost) * 100).toFixed(2)),
                }
              : p
          ),
        }))
      },

      // CALCULATIONS
      getTotalSpent: () => {
        return get().platforms.reduce((sum, p) => sum + p.spent, 0)
      },

      getTotalLeads: () => {
        return get().platforms.reduce((sum, p) => sum + p.leads, 0)
      },

      getAverageROI: () => {
        const platforms = get().platforms
        if (platforms.length === 0) return 0
        const totalROI = platforms.reduce((sum, p) => sum + p.roi, 0)
        return (totalROI / platforms.length).toFixed(2)
      },

      getTopPlatform: () => {
        const platforms = get().platforms
        return platforms.reduce((top, current) =>
          current.spent > (top.spent || 0) ? current : top
        , {})
      },

      getMostEffectivePlatform: () => {
        const platforms = get().platforms
        return platforms.reduce((best, current) =>
          current.roi > (best.roi || 0) ? current : best
        , {})
      },

      getCampaignStats: () => {
        const campaigns = get().campaigns
        return {
          total: campaigns.length,
          active: campaigns.filter((c) => c.status === 'active').length,
          completed: campaigns.filter((c) => c.status === 'completed').length,
          paused: campaigns.filter((c) => c.status === 'paused').length,
          totalBudget: campaigns.reduce((sum, c) => sum + c.budget, 0),
          totalSpent: campaigns.reduce((sum, c) => sum + c.spent, 0),
          totalLeads: campaigns.reduce((sum, c) => sum + c.leads, 0),
        }
      },

      getMarketingMetrics: () => {
        const spent = get().getTotalSpent()
        const leads = get().getTotalLeads()
        const avgROI = get().getAverageROI()
        const budgets = get().budgets
        const campaigns = get().getCampaignStats()

        return {
          totalSpent: spent,
          totalLeads: leads,
          averageROI: avgROI,
          costPerLead: spent > 0 ? (spent / leads).toFixed(2) : 0,
          remainingBudget: budgets.monthly - spent,
          budgetUtilization: ((spent / budgets.monthly) * 100).toFixed(1),
          campaigns,
        }
      },

      // RESET TO DEFAULTS
      resetAllData: () => {
        set({
          platforms: [
            { id: 'facebook', name: 'Facebook', icon: '👍', color: '#1877F2', spent: 2500, leads: 145, roi: 3.2 },
            { id: 'google', name: 'Google Ads', icon: '🔍', color: '#4285F4', spent: 3200, leads: 189, roi: 4.1 },
            { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0A66C2', spent: 1800, leads: 67, roi: 2.8 },
            { id: 'instagram', name: 'Instagram', icon: '📸', color: '#E4405F', spent: 1200, leads: 92, roi: 2.5 },
            { id: 'twitter', name: 'Twitter', icon: '𝕏', color: '#000000', spent: 600, leads: 34, roi: 1.9 },
            { id: 'tiktok', name: 'TikTok', icon: '🎵', color: '#000000', spent: 900, leads: 156, roi: 5.2 },
          ],
          campaigns: [
            { id: 'camp-1', name: 'Q2 Brand Awareness', platform: 'facebook', budget: 5000, spent: 2500, leads: 145, status: 'active', startDate: '2026-04-01', endDate: '2026-06-30' },
            { id: 'camp-2', name: 'B2B Lead Generation', platform: 'linkedin', budget: 3000, spent: 1800, leads: 67, status: 'active', startDate: '2026-04-15', endDate: '2026-07-15' },
            { id: 'camp-3', name: 'Social Media Growth', platform: 'instagram', budget: 2000, spent: 1200, leads: 92, status: 'active', startDate: '2026-04-10', endDate: '2026-06-10' },
          ],
          monthlySpend: [
            { month: 'Jan', facebook: 1800, google: 2400, linkedin: 1200, instagram: 800, twitter: 300, tiktok: 500 },
            { month: 'Feb', facebook: 2000, google: 2600, linkedin: 1400, instagram: 900, twitter: 350, tiktok: 600 },
            { month: 'Mar', facebook: 2200, google: 2800, linkedin: 1600, instagram: 1000, twitter: 400, tiktok: 700 },
            { month: 'Apr', facebook: 2500, google: 3200, linkedin: 1800, instagram: 1200, twitter: 600, tiktok: 900 },
            { month: 'May', facebook: 2700, google: 3400, linkedin: 1900, instagram: 1300, twitter: 650, tiktok: 1000 },
            { month: 'Jun', facebook: 2900, google: 3600, linkedin: 2100, instagram: 1400, twitter: 700, tiktok: 1100 },
          ],
          budgets: {
            monthly: 15000,
            quarterly: 45000,
            annual: 180000,
          },
        })
      },
    }),
    {
      name: 'marketing-store',
      getStorage: () => localStorage,
    }
  )
)
